/**
=========================================================
* Material Dashboard 2 React - v2.1.0
=========================================================

* Product Page: https://www.creative-tim.com/product/material-dashboard-react
* Copyright 2022 Creative Tim (https://www.creative-tim.com)

Coded by www.creative-tim.com

 =========================================================

* The above copyright notice and this permission notice shall be included in all copies or substantial portions of the Software.
*/

/** 
  All of the routes for the Material Dashboard 2 React are added here,
  You can add a new route, customize the routes and delete the routes here.

  Once you add a new route on this file it will be visible automatically on
  the Sidenav.

  For adding a new route you can follow the existing routes in the routes array.
  1. The `type` key with the `collapse` value is used for a route.
  2. The `type` key with the `title` value is used for a title inside the Sidenav. 
  3. The `type` key with the `divider` value is used for a divider between Sidenav items.
  4. The `name` key is used for the name of the route on the Sidenav.
  5. The `key` key is used for the key of the route (It will help you with the key prop inside a loop).
  6. The `icon` key is used for the icon of the route on the Sidenav, you have to add a node.
  7. The `collapse` key is used for making a collapsible item on the Sidenav that has other routes
  inside (nested routes), you need to pass the nested routes inside an array as a value for the `collapse` key.
  8. The `route` key is used to store the route location which is used for the react router.
  9. The `href` key is used to store the external links location.
  10. The `title` key is only for the item with the type of `title` and its used for the title text on the Sidenav.
  10. The `component` key is used to store the component of its route.
*/

// Material Dashboard 2 React layouts
import SignUp from "layouts/authentication/sign-up";

import Login from "auth/login";
import Register from "auth/register";
import ForgotPassword from "auth/forgot-password";
import ResetPassword from "auth/reset-password";
import ConstructionIcon from "@mui/icons-material/Construction";
import VpnKeyIcon from "@mui/icons-material/VpnKey";
import PeopleIcon from "@mui/icons-material/People";
import BadgeIcon from "@mui/icons-material/Badge";
import HardwareIcon from "@mui/icons-material/Hardware";
import FoundationIcon from "@mui/icons-material/Foundation";
import AppRegistrationIcon from "@mui/icons-material/AppRegistration";
import HowToRegIcon from "@mui/icons-material/HowToReg";
import DescriptionIcon from "@mui/icons-material/Description";
import SummarizeIcon from "@mui/icons-material/Summarize";
import RequestQuoteIcon from "@mui/icons-material/RequestQuote";
import ContactPageIcon from "@mui/icons-material/ContactPage";
import AssignmentIcon from "@mui/icons-material/Assignment";
import Diversity3Icon from "@mui/icons-material/Diversity3";

// @mui icons
import Icon from "@mui/material/Icon";
import AreaSelection from "./pages/areaSelection/areaSelection";
// Import lazy components for better performance
import {
	LazyRoleManagement,
	LazyUserInternalManagement,
	LazySupplierManagement,
	LazyWbsManagement,
	LazyYardManagement,
} from "./components/LazyComponents";

// Keep small/frequently used components as regular imports
import RoleAdd from "./pages/roleMenagment/roleAdd";
import RoleEdit from "./pages/roleMenagment/roleEdit";
import UserInternalAdd from "./pages/userManagment/userInternalAdd";
import UserInternalEdit from "./pages/userManagment/userInternalEdit";
import UserExternalAdd from "./pages/userManagment/userExternalAdd";
import UserExternalEdit from "./pages/userManagment/userExternalEdit";
import UserExternalManagment from "./pages/userManagment/userExternalManagment";
import SupplierAssignedSites from "./pages/suppliersManagment/supplierAssignedSites";
import AddSuppliers from "./pages/suppliersManagment/addSuppliers";
import EditSuppliers from "./pages/suppliersManagment/editSuppliers";
import EditWbs from "./pages/wbsManagment/editWbs";
import AddWbs from "./pages/wbsManagment/addWbs";
import YardAdd from "./pages/yardManagment/yardAdd";
import YardEdit from "./pages/yardManagment/yardEdit";
import JournalAppeal from "./pages/yardManagment/journalAppeal";
import JournalDocuments from "./pages/yardManagment/journalDocuments";
import JournalActivityAdd from "./pages/yardManagment/journalActivityAdd";
import JournalActivity from "./pages/yardManagment/journalActivity";
import DocumentScannerIcon from "@mui/icons-material/DocumentScanner";
import GradingIcon from "@mui/icons-material/Grading";
import JournalVerbal from "./pages/yardManagment/journalVerbal";
// Import lazy report components (usually heavy with charts/tables)
import { LazyJournalReport, LazyPayRoll, LazyWbsReport, LazyUserWbsReport, LazyGDL } from "./components/LazyComponents";
// Removed unused security document imports
import SecurityDocumentManagment from "./pages/security/SecurityDocumentManagment";
import UploadFileIcon from "@mui/icons-material/UploadFile";
import SecurityDocumentUpload from "./pages/security/securityDocumentUpload";
import FolderCopyIcon from "@mui/icons-material/FolderCopy";
import ManagmentSecuritySupplier from "./pages/security/managmentSecuritySupplier";
import ManageSecurityDocument from "./pages/security/manageSecurityDocument";
import ChecklistIcon from "@mui/icons-material/Checklist";
import ManagmentSecuritySupplierAdmin from "./pages/security/managmentSecuritySupplierAdmin";
import ManagmentSecurityAreaSupplierAdmin from "./pages/security/managmentSecurityAreaSupplierAdmin";
import ManageYardSecurityAdmin from "./pages/security/manageYardSecurityAdmin";
import ContentCopyIcon from "@mui/icons-material/ContentCopy";
import GeneralDocumentManagment from "./pages/security/generalDocumentManagment";
import GeneralDocumentAdd from "./pages/security/generalDocumentAdd";
// Removed unused security management imports
import NotificationsNoneIcon from "@mui/icons-material/NotificationsNone";
import UserNotification from "./pages/Notification/userNotification";
import ManagmentSecuritySupplierUser from "./pages/security/managmentSecuritySupplierUser";
import ManagementSecurityInternalUser from "./pages/security/managmentSecurityInternalUser";
import ManagementSecurityBottoli from "./pages/security/managmentSecurityBottoli";
import WelcomePage from "./pages/welcomePage";
import InsertDriveFileIcon from "@mui/icons-material/InsertDriveFile";
import ConcreteDLWithdrawalReportAdd from "./pages/quality/concreteDLWithdrawalReportAdd";
import ConcreteDLWithdrawalReportMenagment from "./pages/quality/concreteDLWithdrawalReportMenagment";
import FolderSharedIcon from "@mui/icons-material/FolderShared";
import ConcreteCastingRegistrationManagment from "./pages/quality/concreteCastingRegistrationManagment";
import ConcreteCastingRegistrationAdd from "./pages/quality/concreteCastingRegistrationAdd";
import FormatListBulletedIcon from "@mui/icons-material/FormatListBulleted";
import ViewInArIcon from "@mui/icons-material/ViewInAr";
import ConcreteCubesWithdrawalManagment from "./pages/quality/concreteCubesWithdrawalManagment";
import ConcreteResultManagment from "./pages/quality/concreteResultManagment";
import BiotechIcon from "@mui/icons-material/Biotech";
import ConcreteResultAdd from "./pages/quality/concreteResultAdd";
import SteelWithdrawalReportManagment from "./pages/quality/steelWithdrawalReportManagment";
import SteelWithdrawalReportAdd from "./pages/quality/steelWithdrawalReportAdd";
import SteelWithdrawalRegistrationManagment from "./pages/quality/steelWithdrawalRegistrationManagment";
import SteelWithdrawalRegistrationAdd from "./pages/quality/steelWithdrawalRegistrationAdd";
import SteelSampleManagment from "./pages/quality/steelSampleManagment";
import SteelSampleResultManagment from "./pages/quality/steelSampleResultManagment";
import SteelSampleResultAdd from "./pages/quality/steelSampleResultAdd";
import GarbageFirstCopyManagment from "./pages/quality/garbageFirstCopyManagment";
import GarbageFirstCopyAdd from "./pages/quality/garbageFirstCopyAdd";
import GarbageFourthCopyManagment from "./pages/quality/garbageFourthCopyManagment";
import GarbageFourthCopyAdd from "./pages/quality/garbageFourthCopyAdd";
import GarbageExport from "./pages/quality/garbageExport";
import PianoControlli from "./pages/quality/pianoControlli";
import PianoControlliAdd from "./pages/quality/pianoControlliAdd";
import ControlliEffettuati from "./pages/quality/controlliEffettuati";
import NonConformitaDashboard from "./pages/quality/NonConformitaDashboard";
import NonConformitaAdd from "./pages/quality/NonConformitaAdd";
import NonConformitaSoluzione from "./pages/quality/NonConformitaSoluzione";
import NonConformitaChiusura from "./pages/quality/NonConformitaChiusura";
import VerbaliList from "./pages/meetings/VerbaliList";
import VerbaleEditor from "./pages/meetings/VerbaleEditor";
import FindInPageIcon from "@mui/icons-material/FindInPage";
import FactoryIcon from "@mui/icons-material/Factory";
import HighQualityIcon from "@mui/icons-material/HighQuality";
import ReportProblemIcon from "@mui/icons-material/ReportProblem";
import GroupsIcon from "@mui/icons-material/Groups";
import CheckCircleIcon from "@mui/icons-material/CheckCircle";
import ChangePassword from "pages/changePassword";
import MachineryManagement from "./pages/suppliersManagment/machineryManagement";
import AddMachinery from "./pages/suppliersManagment/addMachinery";
import EditMachinery from "./pages/suppliersManagment/editMachinery";
import BuildIcon from "@mui/icons-material/Build";
import GroupIcon from "@mui/icons-material/Group";
import LocationOnIcon from "@mui/icons-material/LocationOn";
import ExportAttachments from "./pages/stampe/exportAttachments";
import SurveyCreator from "./surveyjs/SurveyCreator";
import SurveyCreatorList from "./surveyjs/SurveyCreatorList";
import Survey from "./surveyjs/SurveyCompiler";
import Creator from "./pages/creator";
import TestUtente from "./pages/testUtente";
import SurveyRunnerList from "./surveyjs/SurveyCompiledList";
// Scadenziario imports
import CategoryList from "./pages/scadenziario/CategoryList";
import CategoryAddEdit from "./pages/scadenziario/CategoryAddEdit";
import CompileScadenziario from "./pages/scadenziario/CompileScadenziario";
import CompiledList from "./pages/scadenziario/CompiledList";
import CategoryIcon from "@mui/icons-material/Category";
import EventIcon from "@mui/icons-material/Event";
import ListAltIcon from "@mui/icons-material/ListAlt";
import SegnalazioniManagement from "./pages/segnalazioni/SegnalazioniManagement";
import SnagListTable from "./pages/snaglist/SnagListTable";
import FloorPlanView from "./pages/snaglist/FloorPlanView";
import SnagItemAdd from "./pages/snaglist/SnagItemAdd";
import FloorPlanManagement from "./pages/snaglist/FloorPlanManagement";
import PlaylistAddCheckIcon from "@mui/icons-material/PlaylistAddCheck";
const routes = [
	// Scadenziario routes
	// {
	// 	type: "Scadenziario",
	// 	name: "Lista Categorie",
	// 	key: "scadenziario-categories",
	// 	icon: <CategoryIcon fontSize="small" />,
	// 	route: "/scadenziario/categories",
	// 	component: <CategoryList />,
	// 	showInSidebar: true,
	// 	auths: ["SELECT_AREA"],
	// },
	// {
	// 	type: "Scadenziario",
	// 	name: "Nuova Categoria",
	// 	key: "scadenziario-category-add",
	// 	route: "/scadenziario/categories/add",
	// 	component: <CategoryAddEdit />,
	// 	showInSidebar: false,
	// 	auths: ["SELECT_AREA"],
	// },
	// {
	// 	type: "Scadenziario",
	// 	name: "Modifica Categoria",
	// 	key: "scadenziario-category-edit",
	// 	route: "/scadenziario/categories/edit/:id",
	// 	component: <CategoryAddEdit />,
	// 	showInSidebar: false,
	// 	auths: ["SELECT_AREA"],
	// },
	// {
	// 	type: "Scadenziario",
	// 	name: "Lista Compilati",
	// 	key: "scadenziario-compiled-list",
	// 	icon: <ListAltIcon fontSize="small" />,
	// 	route: "/scadenziario/compiled-list",
	// 	component: <CompiledList />,
	// 	showInSidebar: true,
	// 	auths: ["SELECT_AREA"],
	// },
	// {
	// 	type: "Scadenziario",
	// 	name: "Compila",
	// 	key: "scadenziario-compile",
	// 	icon: <EventIcon fontSize="small" />,
	// 	route: "/scadenziario/compile",
	// 	component: <CompileScadenziario />,
	// 	showInSidebar: true,
	// 	auths: ["SELECT_AREA"],
	// },
	// {
	// 	type: "Sondaggi",
	// 	name: "Lista Sondaggi",
	// 	key: "survey-creator-list",
	// 	icon: <AssignmentIcon fontSize="small" />,
	// 	route: "/survey-creator/list",
	// 	component: <SurveyCreatorList />,
	// 	showInSidebar: true,
	// 	auths: ["SELECT_AREA"],
	// },
	// {
	// 	type: "Sondaggi",
	// 	name: "Nuovo Sondaggio",
	// 	key: "survey-creator-add",
	// 	route: "/survey-creator/add",
	// 	component: <SurveyCreator />,
	// 	showInSidebar: true,
	// 	auths: ["SELECT_AREA"],
	// },
	// {
	// 	type: "Sondaggi",
	// 	name: "Modifica Sondaggio",
	// 	key: "survey-creator-edit",
	// 	route: "/survey-creator/edit/:id",
	// 	component: <SurveyCreator />,
	// 	showInSidebar: false,
	// 	auths: ["SELECT_AREA"],
	// },
	// {
	// 	type: "Sondaggi",
	// 	name: "Compila Sondaggio",
	// 	key: "survey-runner",
	// 	route: "/survey/:id",
	// 	component: <Survey />,
	// 	showInSidebar: false,
	// 	auths: ["SELECT_AREA"],
	// },
	// {
	// 	type: "Sondaggi",
	// 	name: "Visualizza compilati",
	// 	key: "survey-runner-list",
	// 	route: "/survey-runner/list",
	// 	component: <SurveyRunnerList />,
	// 	showInSidebar: true,
	// 	auths: ["SELECT_AREA"],
	// },
	{
		type: "collapse",
		name: "Test",
		key: "test",
		route: "/creator",
		component: <Creator />,
		showInSidebar: false,
	},
	{
		type: "collapse",
		name: "Test Utente",
		key: "test-utente",
		route: "/test-utente",
		component: <TestUtente />,
		showInSidebar: false,
	},
	{
		type: "collapse",
		name: "Sign Up",
		key: "sign-up",
		icon: <Icon fontSize="small">assignment</Icon>,
		route: "/authentication/sign-up",
		component: <SignUp />,
		showInSidebar: false,
		auths: null,
	},
	{
		type: "auth",
		name: "Login",
		key: "login",
		icon: <Icon fontSize="small">login</Icon>,
		route: "/auth/login",
		component: <Login />,
		showInSidebar: false,
		auths: null,
	},
	{
		type: "collapse",
		name: "Cambia area",
		key: "area-selection",
		icon: <ConstructionIcon fontSize="small"></ConstructionIcon>,
		route: "/area-selection",
		component: <AreaSelection />,
		showInSidebar: true,
		auths: ["SELECT_AREA"],
	},
	{
		type: "Anagrafiche",
		name: "Aggiungi ruolo",
		key: "add-role",
		route: "/role-menagment/add",
		component: <RoleAdd />,
		showInSidebar: false,
		auths: ["ADD_ROLE"],
	},
	{
		type: "Anagrafiche",
		name: "Modifica ruolo",
		key: "edit-role",
		route: "/role-menagment/edit/:id",
		component: <RoleEdit />,
		showInSidebar: false,
		auths: ["EDIT_ROLE"],
	},
	{
		type: "Anagrafiche",
		name: "Operatori Bottoli",
		key: "users-internal-managment",
		icon: <PeopleIcon fontSize="small"></PeopleIcon>,
		route: "/users-internal-managment",
		component: <LazyUserInternalManagement />,
		showInSidebar: true,
		auths: ["VIEW_INTERNAL_USER"],
	},
	{
		type: "Anagrafiche",
		name: "Aggiungi operatore interno",
		key: "add-internal-user",
		route: "/users-internal-managment/add",
		component: <UserInternalAdd />,
		showInSidebar: false,
		auths: ["ADD_INTERNAL_USER"],
	},
	{
		type: "Anagrafiche",
		name: "Modifica operatore interno",
		key: "edit-internal-user",
		route: "/users-internal-managment/edit/:id",
		component: <UserInternalEdit />,
		showInSidebar: false,
		auths: ["EDIT_INTERNAL_USER"],
	},
	{
		type: "Anagrafiche",
		name: "Operatori esterni",
		key: "users-external-managment",
		icon: <Diversity3Icon fontSize="small"></Diversity3Icon>,
		route: "/users-external-managment",
		component: <UserExternalManagment />,
		showInSidebar: true,
		auths: ["VIEW_EXTERNAL_USER_ADMIN"],
	},
	{
		type: "Anagrafiche",
		name: "Aggiungi operatore esterno",
		key: "add-external-user",
		route: "/users-external-managment/add",
		component: <UserExternalAdd />,
		showInSidebar: false,
		auths: ["ADD_EXTERNAL_USER"],
	},
	{
		type: "Anagrafiche",
		name: "Modifica operatore esterno",
		key: "edit-external-user",
		route: "/users-external-managment/edit/:id",
		component: <UserExternalEdit />,
		showInSidebar: false,
		auths: ["EDIT_EXTERNAL_USER"],
	},
	{
		type: "Anagrafiche",
		name: "aggiungi fornitori",
		key: "add-suppliers-managment",
		icon: <BadgeIcon fontSize="small"></BadgeIcon>,
		route: "/suppliers-managment/add",
		component: <AddSuppliers />,
		showInSidebar: false,
		auths: ["ADD_SUPPLIERS"],
	},
	{
		type: "Anagrafiche",
		name: "modifica fornitori",
		key: "edit-suppliers-managment",
		icon: <BadgeIcon fontSize="small"></BadgeIcon>,
		route: "/suppliers-managment/edit/:id",
		component: <EditSuppliers />,
		showInSidebar: false,
		auths: ["EDIT_SUPPLIERS"],
	},
	{
		type: "Anagrafiche",
		name: "Fornitori",
		key: "suppliers-managment",
		icon: <BadgeIcon fontSize="small"></BadgeIcon>,
		route: "/suppliers-managment",
		component: <LazySupplierManagement />,
		showInSidebar: true,
		auths: ["VIEW_SUPPLIERS"],
	},
	{
		type: "Anagrafiche",
		name: "aggiungi macchinario",
		key: "add-machinery-management",
		icon: <BadgeIcon fontSize="small"></BadgeIcon>,
		route: "/machinery-management/add",
		component: <AddMachinery />,
		showInSidebar: false,
		auths: ["EDIT_MACHINERY"],
	},
	{
		type: "Anagrafiche",
		name: "modifica macchinario",
		key: "edit-machinery-management",
		icon: <BadgeIcon fontSize="small"></BadgeIcon>,
		route: "/machinery-management/edit/:id",
		component: <EditMachinery />,
		showInSidebar: false,
		auths: ["EDIT_MACHINERY"],
	},
	{
		type: "Anagrafiche",
		name: "Macchinari",
		key: "machinery-management",
		icon: <BuildIcon fontSize="small"></BuildIcon>,
		route: "/machinery-management",
		component: <MachineryManagement />,
		showInSidebar: true,
		auths: ["VIEW_MACHINERY"],
	},
	{
		type: "Anagrafiche",
		name: "Dipendenti",
		key: "supplier-employees",
		icon: <GroupIcon fontSize="small"></GroupIcon>,
		route: "/supplier-employees",
		component: <UserExternalManagment />,
		showInSidebar: true,
		auths: ["VIEW_SUPPLIER_EMPLOYEES"],
	},
	{
		type: "Anagrafiche",
		name: "Aggiungi dipendente",
		key: "add-supplier-employee",
		route: "/supplier-employees/add",
		component: <UserExternalAdd />,
		showInSidebar: false,
		auths: ["ADD_SUPPLIER_EMPLOYEES"],
	},
	{
		type: "Anagrafiche",
		name: "Modifica dipendente",
		key: "edit-supplier-employee",
		route: "/supplier-employees/edit/:id",
		component: <UserExternalEdit />,
		showInSidebar: false,
		auths: ["EDIT_SUPPLIER_EMPLOYEES"],
	},
	// {
	// 	type: "Anagrafiche",
	// 	name: "Cantieri Assegnati",
	// 	key: "supplier-assigned-sites",
	// 	icon: <LocationOnIcon fontSize="small"></LocationOnIcon>,
	// 	route: "/supplier-assigned-sites",
	// 	component: <SupplierAssignedSites />,
	// 	showInSidebar: true,
	// 	auths: ["VIEW_SUPPLIER_SITES"],
	// },
	{
		type: "Anagrafiche",
		name: "aggiungi wbs",
		key: "add-wbs-managment",
		icon: <BadgeIcon fontSize="small"></BadgeIcon>,
		route: "/wbs-managment/add",
		component: <AddWbs />,
		showInSidebar: false,
		auths: ["ADD_JOBS"],
	},
	{
		type: "Anagrafiche",
		name: "modifica wbs",
		key: "edit-wbs-managment",
		icon: <BadgeIcon fontSize="small"></BadgeIcon>,
		route: "/wbs-managment/edit/:id",
		component: <EditWbs />,
		showInSidebar: false,
		auths: ["EDIT_JOBS"],
	},
	{
		type: "Anagrafiche",
		name: "Wbs",
		key: "wbs-managment",
		icon: <HardwareIcon fontSize="small"></HardwareIcon>,
		route: "/wbs-managment",
		component: <LazyWbsManagement />,
		showInSidebar: true,
		auths: ["VIEW_JOBS"],
	},
	{
		type: "Anagrafiche",
		name: "aggiungi cantiere",
		key: "add-yard-managment",
		icon: <BadgeIcon fontSize="small"></BadgeIcon>,
		route: "/yard-managment/add",
		component: <YardAdd />,
		showInSidebar: false,
		auths: ["ADD_AREE"],
	},
	{
		type: "Anagrafiche",
		name: "modifica cantiere",
		key: "edit-yard-managment",
		icon: <BadgeIcon fontSize="small"></BadgeIcon>,
		route: "/yard-managment/edit/:id",
		component: <YardEdit />,
		showInSidebar: false,
		auths: ["EDIT_AREE"],
	},
	{
		type: "Anagrafiche",
		name: "Cantiere",
		key: "yard-managment",
		icon: <FoundationIcon fontSize="small"></FoundationIcon>,
		route: "/yard-managment",
		component: <LazyYardManagement />,
		showInSidebar: true,
		auths: ["VIEW_AREE"],
	},
	{
		type: "Anagrafiche",
		name: "Ruoli",
		key: "role-menagment",
		icon: <VpnKeyIcon fontSize="small"></VpnKeyIcon>,
		route: "/role-menagment",
		component: <LazyRoleManagement />,
		showInSidebar: true,
		auths: ["VIEW_ROLE"],
	},
	{
		type: "Gestione cantiere",
		name: "Registro presenze",
		key: "journal-appeal",
		icon: <HowToRegIcon fontSize="small"></HowToRegIcon>,
		route: "/journal-appeal",
		component: <JournalAppeal />,
		showInSidebar: true,
		auths: ["VIEW_USERAPPEAL"],
	},
	{
		type: "Gestione cantiere",
		name: "Attività giornaliere",
		key: "journal-activity",
		icon: <AppRegistrationIcon fontSize="small"></AppRegistrationIcon>,
		route: "/journal-activity",
		component: <JournalActivity />,
		showInSidebar: true,
		auths: ["VIEW_DAILYACTIVITY"],
	},
	{
		type: "Gestione cantiere",
		name: "Aggiungi attività giornaliere",
		key: "journal-activity-add",
		icon: <AppRegistrationIcon fontSize="small"></AppRegistrationIcon>,
		route: "/journal-appeal/add/:id/:date",
		component: <JournalActivityAdd />,
		showInSidebar: false,
		auths: ["ADD_DAILYACTIVITY"],
	},
	{
		type: "Gestione cantiere",
		name: "Aggiungi attività giornaliere",
		key: "journal-activity-add",
		icon: <AppRegistrationIcon fontSize="small"></AppRegistrationIcon>,
		route: "/journal-activity/add/:id/:date",
		component: <JournalActivityAdd />,
		showInSidebar: false,
		auths: ["ADD_DAILYACTIVITY"],
	},

	{
		type: "Gestione cantiere",
		name: "Allegati giornalieri",
		key: "juournal-attachments",
		icon: <DescriptionIcon fontSize="small"></DescriptionIcon>,
		route: "/juournal-attachments",
		component: <JournalDocuments />,
		showInSidebar: true,
		auths: ["VIEW_ATTACHMENTAREA"],
	},
	{
		type: "Gestione cantiere",
		name: "Verbali giornalieri",
		key: "juournal-verbal",
		icon: <DocumentScannerIcon fontSize="small"></DocumentScannerIcon>,
		route: "/juournal-verbal",
		component: <JournalVerbal />,
		showInSidebar: true,
		auths: ["VIEW_VERBALAREA"],
	},
	{
		type: "Gestione cantiere",
		name: "Controlli effettuati",
		key: "controlli-effettuati",
		icon: <CheckCircleIcon fontSize="small"></CheckCircleIcon>,
		route: "/controlli-effettuati",
		component: <ControlliEffettuati />,
		showInSidebar: true,
		auths: ["VIEW_DAILYACTIVITY"],
	},
	{
		type: "Gestione cantiere",
		name: "Segnalazioni",
		key: "segnalazioni",
		icon: <ReportProblemIcon fontSize="small" />,
		route: "/segnalazioni",
		component: <SegnalazioniManagement />,
		showInSidebar: true,
		auths: ["VIEW_SEGNALAZIONI"],
	},
	{
		type: "Gestione cantiere",
		name: "Snag List",
		key: "snag-list",
		icon: <PlaylistAddCheckIcon fontSize="small" />,
		route: "/snag-list",
		component: <SnagListTable />,
		showInSidebar: true,
		auths: ["VIEW_SNAGLIST"],
	},
	{
		type: "Gestione cantiere",
		name: "Planimetria Snag",
		key: "snag-list-floor-plan",
		route: "/snag-list/floor-plan/:areaId/:floorPlanId",
		component: <FloorPlanView />,
		showInSidebar: false,
		auths: ["VIEW_SNAGLIST"],
	},
	{
		type: "Gestione cantiere",
		name: "Nuovo Snag",
		key: "snag-list-add",
		route: "/snag-list/add/:floorPlanId",
		component: <SnagItemAdd />,
		showInSidebar: false,
		auths: ["ADD_SNAGLIST"],
	},
	{
		type: "Gestione cantiere",
		name: "Gestione Planimetrie",
		key: "snag-list-floor-plans",
		route: "/snag-list/floor-plans",
		component: <FloorPlanManagement />,
		showInSidebar: false,
		auths: ["VIEW_SNAGLIST"],
	},
	{
		type: "Esporta",
		name: "Rapportino giornaliero",
		key: "juournal-report",
		icon: <SummarizeIcon fontSize="small"></SummarizeIcon>,
		route: "/juournal-report",
		component: <LazyJournalReport />,
		showInSidebar: true,
		auths: ["VIEW_JOURNALREPORT"],
	},
	{
		type: "Esporta",
		name: "Paghe",
		key: "payroll",
		icon: <RequestQuoteIcon fontSize="small"></RequestQuoteIcon>,
		route: "/payroll",
		component: <LazyPayRoll />,
		showInSidebar: true,
		auths: ["VIEW_PAYROLL"],
	},
	{
		type: "Esporta",
		name: "Wbs Report",
		key: "wbs-report",
		icon: <RequestQuoteIcon fontSize="small"></RequestQuoteIcon>,
		route: "/wbs-report",
		component: <LazyWbsReport />,
		showInSidebar: true,
		auths: ["VIEW_WBSREPORT"],
	},
	{
		type: "Esporta",
		name: "Report Wbs Dipendenti",
		key: "user-wbs-report",
		icon: <ContactPageIcon fontSize="small"></ContactPageIcon>,
		route: "/user-wbs-report",
		component: <LazyUserWbsReport />,
		showInSidebar: true,
		auths: ["VIEW_WBSUSERREPORT"],
	},
	{
		type: "Esporta",
		name: "GDL",
		key: "gdl",
		icon: <ContactPageIcon fontSize="small"></ContactPageIcon>,
		route: "/gdl",
		component: <LazyGDL />,
		showInSidebar: true,
		auths: ["VIEW_GDL"],
	},
	{
		type: "Esporta",
		name: "Esporta allegati",
		key: "export-attachments",
		icon: <ContactPageIcon fontSize="small"></ContactPageIcon>,
		route: "/export-attachments",
		component: <ExportAttachments />,
		showInSidebar: true,
		auths: ["VIEW_ATTACHMENTAREA"],
	},
	{
		type: "auth",
		name: "Register",
		key: "register",
		icon: <Icon fontSize="small">reigster</Icon>,
		route: "/auth/register",
		component: <Register />,
		showInSidebar: true,
		auths: null,
	},
	{
		type: "auth",
		name: "Forgot Password",
		key: "forgot-password",
		icon: <Icon fontSize="small">assignment</Icon>,
		route: "/auth/forgot-password",
		component: <ForgotPassword />,
		showInSidebar: true,
		auths: null,
	},
	{
		type: "auth",
		name: "Reset Password",
		key: "reset-password",
		icon: <Icon fontSize="small">assignment</Icon>,
		route: "/auth/reset-password",
		component: <ResetPassword />,
		showInSidebar: true,
		auths: null,
	},
	/*{
    type: "Sicurezza",
    name: "Documenti cantieri",
    key: "security-document-area",
    icon: <GradingIcon fontSize="small"></GradingIcon>,
    route: "/security-document-area",
    component: <SecurityDocumentArea/>,
    showInSidebar:true,
    auths: ["VIEW_DOCUMENTS_AREA"]
  },
    {
    type: "Sicurezza",
    name: "Documenti cantiere",
    key: "security-document-area-add",
    icon: <GradingIcon fontSize="small"></GradingIcon>,
    route: "/security-document-area-add/:id/:areaName",
    component: <SecurityDocumentAreaAdd/>,
    showInSidebar:false,
    auths: ["VIEW_DOCUMENTS_AREA"]
  },
  {
    type: "Sicurezza",
    name: "Documenti cantiere",
    key: "security-document-area-add",
    icon: <GradingIcon fontSize="small"></GradingIcon>,
    route: "/security-document-area-add/:id/:areaName/:idDocument",
    component: <SecurityDocumentAreaAdd/>,
    showInSidebar:false,
    auths: ["VIEW_DOCUMENTS_AREA"]
  },*/
	{
		type: "Sicurezza",
		name: "Gestione documenti cantiere",
		key: "security-document-managment",
		icon: <GradingIcon fontSize="small"></GradingIcon>,
		route: "/security-document-managment/:id/:areaName",
		component: <SecurityDocumentManagment />,
		showInSidebar: false,
		auths: ["VIEW_DOCUMENTS_AREA"],
	},
	{
		type: "Sicurezza",
		name: "Carica documenti",
		key: "security-document-upload",
		icon: <UploadFileIcon fontSize="small"></UploadFileIcon>,
		route: "/security-document-upload/:type/:istanceName/:idIstance/", // Da usare per gli utenti dei fornitori
		component: <SecurityDocumentUpload />,
		showInSidebar: false,
		auths: ["UPSERT_DOCUMENTS_SUPPLIER"],
	},
	{
		type: "Sicurezza",
		name: "Carica documenti",
		key: "security-document-upload",
		icon: <UploadFileIcon fontSize="small"></UploadFileIcon>,
		route: "/security-document-upload/:type/", // da usare per i documenti generali del fornitore
		component: <SecurityDocumentUpload />,
		showInSidebar: false,
		auths: ["UPSERT_DOCUMENTS_SUPPLIER"],
	},
	{
		type: "Sicurezza",
		name: "Documenti azienda",
		key: "security-document-supplier/",
		icon: <FolderCopyIcon fontSize="small"></FolderCopyIcon>,
		route: "/security-document-supplier/",
		component: <ManagmentSecuritySupplier />,
		showInSidebar: true,
		auths: ["VIEW_DOCUMENTS_SUPPLIER"],
		submenu: "Fornitori",
	},
	{
		type: "Sicurezza",
		name: "Documenti operatori",
		key: "security-document-supplier-user/",
		icon: <FolderSharedIcon fontSize="small"></FolderSharedIcon>,
		route: "/security-document-supplier-user/",
		component: <ManagmentSecuritySupplierUser />,
		showInSidebar: true,
		auths: ["VIEW_DOCUMENTS_SUPPLIER"],
		submenu: "Fornitori",
	},
	{
		type: "Sicurezza",
		name: "Documenti operatori",
		key: "security-document-internal-user/",
		icon: <PeopleIcon fontSize="small"></PeopleIcon>,
		route: "/security-document-internal-user/",
		component: <ManagementSecurityInternalUser />,
		showInSidebar: true,
		auths: ["VIEW_DOCUMENTS_SUPPLIER"],
		submenu: "Bottoli",
	},
	{
		type: "Sicurezza",
		name: "Documenti azienda",
		key: "security-document-bottoli/",
		icon: <FolderCopyIcon fontSize="small"></FolderCopyIcon>,
		route: "/security-document-bottoli/",
		component: <ManagementSecurityBottoli />,
		showInSidebar: true,
		auths: ["VIEW_DOCUMENTS_SUPPLIER"],
		submenu: "Bottoli",
	},
	/*{
    type: "Sicurezza",
    name: "Documenti azienda",
    key: "security-document-area-supplier",
    icon: <FolderCopyIcon fontSize="small"></FolderCopyIcon>,
    route: "/security-document-area-supplier/",
    component: <ManagmentSecurityAreaSupplier/>,
    showInSidebar:false,
    auths: ["VIEW_DOCUMENTS_SUPPLIER"]
  },*/
	{
		type: "Sicurezza",
		name: "Approvazione documenti",
		key: "security-document-approval",
		icon: <ChecklistIcon fontSize="small"></ChecklistIcon>,
		route: "/security-document-approval/:type/:id/:instanceName",
		component: <ManageSecurityDocument />,
		showInSidebar: false,
		auths: ["UPSERT_SECURITY_MANAGMENT"],
	},
	{
		type: "Sicurezza",
		name: "Gestisci fornitore",
		key: "security-document-supplier-admin",
		icon: <ChecklistIcon fontSize="small"></ChecklistIcon>,
		route: "/security-document-supplier-admin/:id/:supplierName/",
		component: <ManagmentSecuritySupplierAdmin />,
		showInSidebar: false,
		auths: ["VIEW_SECURITY_MANAGMENT"],
	},
	{
		type: "Sicurezza",
		name: "Gestisci fornitore",
		key: "security-document-supplier-admin",
		icon: <ChecklistIcon fontSize="small"></ChecklistIcon>,
		route: "/security-document-supplier-internal-admin/",
		component: <ManagmentSecuritySupplierAdmin />,
		showInSidebar: false,
		auths: ["VIEW_SECURITY_MANAGMENT"],
	},
	{
		type: "Sicurezza",
		name: "Seleziona fornitore per cantiere",
		key: "security-area-supplier-admin",
		icon: <ChecklistIcon fontSize="small"></ChecklistIcon>,
		route: "/security-area-supplier-admin/:areaName/:idArea",
		component: <ManagmentSecurityAreaSupplierAdmin />,
		showInSidebar: false,
		auths: ["VIEW_SECURITY_MANAGMENT"],
	},
	{
		type: "Sicurezza",
		name: "Gestione sicurezza",
		key: "manage-security-area-supplier-admin/",
		icon: <ChecklistIcon fontSize="small"></ChecklistIcon>,
		route: "/manage-security-area-supplier-admin/",
		component: <ManageYardSecurityAdmin />,
		showInSidebar: true,
		auths: ["VIEW_SECURITY_MANAGMENT"],
	},
	{
		type: "Sicurezza",
		name: "Modelli documenti",
		key: "manage-general-document/",
		icon: <ContentCopyIcon fontSize="small"></ContentCopyIcon>,
		route: "/manage-general-document/",
		component: <GeneralDocumentManagment />,
		showInSidebar: true,
		auths: ["VIEW_GENERAL_DOCUMENTS"],
	},
	{
		type: "Sicurezza",
		name: "Documenti cantiere",
		key: "general-document-add",
		icon: <GradingIcon fontSize="small"></GradingIcon>,
		route: "/general-document-add/",
		component: <GeneralDocumentAdd />,
		showInSidebar: false,
		auths: ["UPSERT_GENERAL_DOCUMENTS"],
	},
	{
		type: "Sicurezza",
		name: "Documenti cantiere",
		key: "general-document-add",
		icon: <GradingIcon fontSize="small"></GradingIcon>,
		route: "/general-document-add/:idDocument",
		component: <GeneralDocumentAdd />,
		showInSidebar: false,
		auths: ["UPSERT_GENERAL_DOCUMENTS"],
	},
	{
		type: "collapse",
		name: "Notifiche",
		key: "notifications",
		icon: <NotificationsNoneIcon fontSize="small"></NotificationsNoneIcon>,
		route: "/notifications",
		component: <UserNotification />,
		showInSidebar: false,
		auths: ["SELECT_AREA"],
	},
	{
		type: "collapse",
		name: "Welcome",
		key: "welcome",
		icon: <NotificationsNoneIcon fontSize="small"></NotificationsNoneIcon>,
		route: "/",
		component: <WelcomePage />,
		showInSidebar: false,
		auths: null,
	},
	{
		type: "Qualità",
		name: "Piano dei controlli",
		key: "piano-controlli",
		icon: <HighQualityIcon fontSize="small"></HighQualityIcon>,
		route: "/piano-controlli",
		component: <PianoControlli />,
		showInSidebar: true,
		auths: null,
	},
	{
		type: "Qualità",
		name: "Aggiungi controllo",
		key: "piano-controlli-add",
		icon: <HighQualityIcon fontSize="small"></HighQualityIcon>,
		route: "/piano-controlli/add",
		component: <PianoControlliAdd />,
		showInSidebar: false,
		auths: ["ADD_PIANO_CONTROLLI"],
	},
	{
		type: "Qualità",
		name: "Modifica controllo",
		key: "piano-controlli-edit",
		icon: <HighQualityIcon fontSize="small"></HighQualityIcon>,
		route: "/piano-controlli/edit/:id",
		component: <PianoControlliAdd />,
		showInSidebar: false,
		auths: ["EDIT_PIANO_CONTROLLI"],
	},
	{
		type: "Qualità",
		name: "Non Conformità",
		key: "non-conformita-dashboard",
		icon: <ReportProblemIcon fontSize="small"></ReportProblemIcon>,
		route: "/quality/non-conformita",
		component: <NonConformitaDashboard />,
		showInSidebar: true,
		auths: ["VIEW_NON_CONFORMITA"],
	},
	{
		type: "Qualità",
		name: "Nuova Non Conformità",
		key: "non-conformita-add",
		icon: <ReportProblemIcon fontSize="small"></ReportProblemIcon>,
		route: "/quality/non-conformita/add",
		component: <NonConformitaAdd />,
		showInSidebar: false,
		auths: ["ADD_NON_CONFORMITA"],
	},
	{
		type: "Qualità",
		name: "Modifica Non Conformità",
		key: "non-conformita-edit",
		icon: <ReportProblemIcon fontSize="small"></ReportProblemIcon>,
		route: "/quality/non-conformita/edit/:id",
		component: <NonConformitaAdd />,
		showInSidebar: false,
		auths: ["EDIT_NON_CONFORMITA"],
	},
	{
		type: "Qualità",
		name: "Proponi Soluzione NC",
		key: "non-conformita-soluzione",
		icon: <ReportProblemIcon fontSize="small"></ReportProblemIcon>,
		route: "/quality/non-conformita/:id/soluzione",
		component: <NonConformitaSoluzione />,
		showInSidebar: false,
		auths: ["EDIT_NON_CONFORMITA"],
	},
	{
		type: "Qualità",
		name: "Chiudi NC",
		key: "non-conformita-chiusura",
		icon: <ReportProblemIcon fontSize="small"></ReportProblemIcon>,
		route: "/quality/non-conformita/:id/chiusura",
		component: <NonConformitaChiusura />,
		showInSidebar: false,
		auths: ["EDIT_NON_CONFORMITA"],
	},
	{
		type: "Qualità",
		name: "Verbali Riunione",
		key: "verbali-riunione-list",
		icon: <GroupsIcon fontSize="small"></GroupsIcon>,
		route: "/meetings/verbali",
		component: <VerbaliList />,
		showInSidebar: true,
		auths: ["VIEW_VERBALI_RIUNIONE"],
	},
	{
		type: "Qualità",
		name: "Nuovo Verbale",
		key: "verbale-riunione-new",
		icon: <GroupsIcon fontSize="small"></GroupsIcon>,
		route: "/meetings/verbali/new",
		component: <VerbaleEditor />,
		showInSidebar: false,
		auths: ["ADD_VERBALI_RIUNIONE"],
	},
	{
		type: "Qualità",
		name: "Modifica Verbale",
		key: "verbale-riunione-edit",
		icon: <GroupsIcon fontSize="small"></GroupsIcon>,
		route: "/meetings/verbali/:id/edit",
		component: <VerbaleEditor />,
		showInSidebar: false,
		auths: ["EDIT_VERBALI_RIUNIONE"],
	},
	{
		type: "Qualità > Calcestruzzo",
		name: "Verbali prelievo",
		key: "concrete-dl-withdrawal-report-add",
		icon: <InsertDriveFileIcon fontSize="small"></InsertDriveFileIcon>,
		route: "/concrete-dl-withdrawal-report-add",
		component: <ConcreteDLWithdrawalReportAdd />,
		showInSidebar: false,
		auths: ["UPDATE_CONCRETEDLWITHDRAWALREPORT"],
	},
	{
		type: "Qualità > Calcestruzzo",
		name: "Verbali prelievo",
		key: "concrete-dl-withdrawal-report-edit",
		icon: <InsertDriveFileIcon fontSize="small"></InsertDriveFileIcon>,
		route: "/concrete-dl-withdrawal-report-edit/:id/",
		component: <ConcreteDLWithdrawalReportAdd />,
		showInSidebar: false,
		auths: ["UPDATE_CONCRETEDLWITHDRAWALREPORT"],
	},
	{
		type: "Qualità > Calcestruzzo",
		name: "Verbali prelievo",
		key: "concrete-dl-withdrawal-report-managment",
		icon: <InsertDriveFileIcon fontSize="small"></InsertDriveFileIcon>,
		route: "/concrete-dl-withdrawal-report-managment",
		component: <ConcreteDLWithdrawalReportMenagment />,
		showInSidebar: true,
		auths: ["VIEW_CONCRETEDLWITHDRAWALREPORT"],
	},
	{
		type: "Qualità > Calcestruzzo",
		name: "Elenco getti",
		key: "concrete-casting-registration-managment",
		icon: <FormatListBulletedIcon fontSize="small"></FormatListBulletedIcon>,
		route: "/concrete-casting-registration-managment",
		component: <ConcreteCastingRegistrationManagment />,
		showInSidebar: true,
		auths: ["VIEW_CONCRETECASTINGREGISTRATION"],
	},
	{
		type: "Qualità > Calcestruzzo",
		name: "Registrazioni getti",
		key: "concrete-casting-registration-managment",
		icon: <InsertDriveFileIcon fontSize="small"></InsertDriveFileIcon>,
		route: "/concrete-casting-registration-managment/add/",
		component: <ConcreteCastingRegistrationAdd />,
		showInSidebar: false,
		auths: ["UPDATE_CONCRETECASTINGREGISTRATION"],
	},
	{
		type: "Qualità > Calcestruzzo",
		name: "Registrazioni getti",
		key: "concrete-casting-registration-managment",
		icon: <InsertDriveFileIcon fontSize="small"></InsertDriveFileIcon>,
		route: "/concrete-casting-registration-managment/edit/:id/",
		component: <ConcreteCastingRegistrationAdd />,
		showInSidebar: false,
		auths: ["UPDATE_CONCRETECASTINGREGISTRATION"],
	},
	{
		type: "Qualità > Calcestruzzo",
		name: "Cubi",
		key: "concrete-cubes",
		icon: <ViewInArIcon fontSize="small"></ViewInArIcon>,
		route: "/concrete-cubes",
		component: <ConcreteCubesWithdrawalManagment />,
		showInSidebar: true,
		auths: ["VIEW_CONCRETECUBESWITHDRAWAL"],
	},
	{
		type: "Qualità > Calcestruzzo",
		name: "Cubi",
		key: "concrete-cubes",
		icon: <ViewInArIcon fontSize="small"></ViewInArIcon>,
		route: "/concrete-cubes/selected/:id/",
		component: <ConcreteCubesWithdrawalManagment />,
		showInSidebar: false,
		auths: ["VIEW_CONCRETECUBESWITHDRAWAL"],
	},
	{
		type: "Qualità > Calcestruzzo",
		name: "Risultati laboratorio",
		key: "concrete-result-managment/",
		icon: <BiotechIcon fontSize="small"></BiotechIcon>,
		route: "/concrete-result-managment/",
		component: <ConcreteResultManagment />,
		showInSidebar: true,
		auths: ["VIEW_CONCRETERESULT"],
	},
	{
		type: "Qualità > Calcestruzzo",
		name: "Risultati laboratorio",
		key: "concrete-result-managment",
		icon: <BiotechIcon fontSize="small"></BiotechIcon>,
		route: "/concrete-result-managment/add/",
		component: <ConcreteResultAdd />,
		showInSidebar: false,
		auths: ["UPDATE_CONCRETERESULT"],
	},
	{
		type: "Qualità > Calcestruzzo",
		name: "Risultati laboratorio",
		key: "concrete-result-managment",
		icon: <BiotechIcon fontSize="small"></BiotechIcon>,
		route: "/concrete-result-managment/edit/:id/",
		component: <ConcreteResultAdd />,
		showInSidebar: false,
		auths: ["UPDATE_CONCRETERESULT"],
	},
	{
		type: "Qualità > Acciaio",
		name: "Verbali prelievo",
		key: "steel-withdrawal-report-managment",
		icon: <InsertDriveFileIcon fontSize="small"></InsertDriveFileIcon>,
		route: "/steel-withdrawal-report-managment/",
		component: <SteelWithdrawalReportManagment />,
		showInSidebar: true,
		auths: ["VIEW_STEELWITHDRAWALREPORT"],
	},
	{
		type: "Qualità > Acciaio",
		name: "Verbali prelievo",
		key: "steel-withdrawal-report-add",
		icon: <InsertDriveFileIcon fontSize="small"></InsertDriveFileIcon>,
		route: "/steel-withdrawal-report-add",
		component: <SteelWithdrawalReportAdd />,
		showInSidebar: false,
		auths: ["UPDATE_STEELWITHDRAWALREPORT"],
	},
	{
		type: "Qualità > Acciaio",
		name: "Verbali prelievo",
		key: "steel-withdrawal-report-edit",
		icon: <InsertDriveFileIcon fontSize="small"></InsertDriveFileIcon>,
		route: "/steel-withdrawal-report-edit/:id/",
		component: <SteelWithdrawalReportAdd />,
		showInSidebar: false,
		auths: ["UPDATE_STEELWITHDRAWALREPORT"],
	},
	{
		type: "Qualità > Acciaio",
		name: "Elenco prelievi",
		key: "steel-withdrawal-registration-managment",
		icon: <FormatListBulletedIcon fontSize="small"></FormatListBulletedIcon>,
		route: "/steel-withdrawal-registration-managment",
		component: <SteelWithdrawalRegistrationManagment />,
		showInSidebar: true,
		auths: ["VIEW_STEELWITHDRAWALREGISTRATION"],
	},
	{
		type: "Qualità > Acciaio",
		name: "elenco prelievi",
		key: "concrete-casting-registration-managment",
		icon: <InsertDriveFileIcon fontSize="small"></InsertDriveFileIcon>,
		route: "/steel-withdrawal-registration-managment/add/",
		component: <SteelWithdrawalRegistrationAdd />,
		showInSidebar: false,
		auths: ["UPDATE_STEELWITHDRAWALREGISTRATION"],
	},
	{
		type: "Qualità > Acciaio",
		name: "elenco prelievi",
		key: "concrete-casting-registration-managment",
		icon: <InsertDriveFileIcon fontSize="small"></InsertDriveFileIcon>,
		route: "/steel-withdrawal-registration-managment/edit/:id/",
		component: <SteelWithdrawalRegistrationAdd />,
		showInSidebar: false,
		auths: ["UPDATE_STEELWITHDRAWALREGISTRATION"],
	},
	{
		type: "Qualità > Acciaio",
		name: "Campioni",
		key: "steel-samples",
		icon: <ViewInArIcon fontSize="small"></ViewInArIcon>,
		route: "/steel-samples",
		component: <SteelSampleManagment />,
		showInSidebar: true,
		auths: ["VIEW_STEELSAMPLE"],
	},
	{
		type: "Qualità > Acciaio",
		name: "Campioni",
		key: "steel-samples",
		icon: <ViewInArIcon fontSize="small"></ViewInArIcon>,
		route: "/steel-samples/selected/:id/",
		component: <SteelSampleManagment />,
		showInSidebar: false,
		auths: ["VIEW_STEELSAMPLE"],
	},
	{
		type: "Qualità > Acciaio",
		name: "Risultati laboratorio",
		key: "concrete-result-managment/",
		icon: <BiotechIcon fontSize="small"></BiotechIcon>,
		route: "/steel-sample-result-managment/",
		component: <SteelSampleResultManagment />,
		showInSidebar: true,
		auths: ["VIEW_STEELSAMPLERESULT"],
	},
	{
		type: "Qualità > Acciaio",
		name: "Risultati laboratorio",
		key: "concrete-result-managment",
		icon: <BiotechIcon fontSize="small"></BiotechIcon>,
		route: "/steel-sample-result-managment/add/",
		component: <SteelSampleResultAdd />,
		showInSidebar: false,
		auths: ["UPDATE_STEELSAMPLERESULT"],
	},
	{
		type: "Qualità > Acciaio",
		name: "Risultati laboratorio",
		key: "concrete-result-managment",
		icon: <BiotechIcon fontSize="small"></BiotechIcon>,
		route: "/steel-sample-result-managment/edit/:id/",
		component: <SteelSampleResultAdd />,
		showInSidebar: false,
		auths: ["UPDATE_STEELSAMPLERESULT"],
	},
	{
		type: "Rifiuti",
		name: "Prima copia",
		key: "garbage-first-copy-managment",
		icon: <DescriptionIcon fontSize="small"></DescriptionIcon>,
		route: "/garbage-first-copy-managment",
		component: <GarbageFirstCopyManagment />,
		showInSidebar: true,
		auths: ["VIEW_GARBAGEFIRSTCOPY"],
	},
	{
		type: "Rifiuti",
		name: "Prima copia",
		key: "garbage-first-copy-managment",
		icon: <DescriptionIcon fontSize="small"></DescriptionIcon>,
		route: "/garbage-first-copy-managment/add/",
		component: <GarbageFirstCopyAdd />,
		showInSidebar: false,
		auths: ["UPDATE_GARBAGEFIRSTCOPY"],
	},
	{
		type: "Rifiuti",
		name: "Prima copia",
		key: "garbage-first-copy-managment",
		icon: <DescriptionIcon fontSize="small"></DescriptionIcon>,
		route: "/garbage-first-copy-managment/edit/:id/",
		component: <GarbageFirstCopyAdd />,
		showInSidebar: false,
		auths: ["UPDATE_GARBAGEFIRSTCOPY"],
	},
	{
		type: "Rifiuti",
		name: "Quarta copia",
		key: "garbage-fourth-copy-managment",
		icon: <DocumentScannerIcon fontSize="small"></DocumentScannerIcon>,
		route: "/garbage-fourth-copy-managment/",
		component: <GarbageFourthCopyManagment />,
		showInSidebar: true,
		auths: ["VIEW_GARBAGEFOURTHCOPY"],
	},
	{
		type: "Rifiuti",
		name: "quarta copia",
		key: "garbage-fourth-copy-managment",
		icon: <DocumentScannerIcon fontSize="small"></DocumentScannerIcon>,
		route: "/garbage-fourth-copy-managment/add/",
		component: <GarbageFourthCopyAdd />,
		showInSidebar: false,
		auths: ["UPDATE_GARBAGEFOURTHCOPY"],
	},
	{
		type: "Rifiuti",
		name: "quarta copia",
		key: "garbage-fourth-copy-managment",
		icon: <DescriptionIcon fontSize="small"></DescriptionIcon>,
		route: "/garbage-fourth-copy-managment/edit/:id/",
		component: <GarbageFourthCopyAdd />,
		showInSidebar: false,
		auths: ["UPDATE_GARBAGEFOURTHCOPY"],
	},
	{
		type: "Rifiuti",
		name: "Ricerca documenti",
		key: "garbage-export",
		icon: <FindInPageIcon fontSize="small"></FindInPageIcon>,
		route: "/garbage-export",
		component: <GarbageExport />,
		showInSidebar: true,
		auths: ["VIEW_GARBAGEEXPORT"],
	},
	// change password
	{
		type: "change password",
		name: "change password",
		key: "change-password",
		icon: null,
		route: "/change-password",
		component: <ChangePassword />,
		showInSidebar: true,
		auths: null,
	},
];

export default routes;
